import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';

// MUI
import { Grid, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RedoIcon from '@mui/icons-material/Redo';
import {
  Dropdown,
  InputLabel,
  OpenWorkflowText,
  BlueTextCenter,
  StyledOpenInNewIcon,
  BoldText,
  DropdownContainer,
  BlackRedoIcon,
  StyledLink,
} from './StyledComponents';

function Dropdowns(props) {
  const [projectRefreshSuccess, setProjectRefreshSuccess] = useState(false);
  const [componentRefreshSuccess, setComponentRefreshSuccess] = useState(false);

  // Reset success indicators after a delay
  useEffect(() => {
    let timer;
    if (projectRefreshSuccess) {
      timer = setTimeout(() => {
        setProjectRefreshSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [projectRefreshSuccess]);

  useEffect(() => {
    let timer;
    if (componentRefreshSuccess) {
      timer = setTimeout(() => {
        setComponentRefreshSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [componentRefreshSuccess]);

  const handleProjectRefresh = async () => {
    if (props.refreshProjects) {
      const result = await props.refreshProjects();
      if (result) {
        setProjectRefreshSuccess(true);
      }
    }
  };

  const handleComponentRefresh = async () => {
    if (props.refreshComponents) {
      const result = await props.refreshComponents();
      if (result !== false) {
        setComponentRefreshSuccess(true);
      }
    }
  };

  const handleSelectProject = newProjectId => {
    props
      .updateState({
        sourceProjectId: newProjectId,
        sourceComponentId: '',
        connectionData: null,
      })
      .then(() => props.handleSaveComponent());
    props.getComponents(newProjectId);
  };

  const handleSelectComponent = newComponentId => {
    props.updateState({ sourceComponentId: newComponentId }).then(() => {
      // Automatically trigger the connection when a component is selected
      if (props.connectTriggers) {
        props.connectTriggers();
      }
    });
  };

  const getProjectLabel = projId => {
    let proj = props.projects.find(proj => proj.id === projId);
    return proj;
  };

  const getComponentLabel = compId => {
    let comp = props.components.find(comp => comp.id === compId);
    return comp;
  };

  const renderProjectDropdown = () => {
    return (
      <Grid item xs={12}>
        <Grid container alignItems="center" justifyContent="space-between">
          <InputLabel>
            Select the <BoldText>Source Workflow </BoldText> that will trigger this component
          </InputLabel>
          <Tooltip title={props.projectsRefreshing ? 'Refreshing projects...' : 'Refresh projects'}>
            <span>
              <IconButton
                size="small"
                onClick={handleProjectRefresh}
                aria-label="refresh projects"
                disabled={props.projectsRefreshing}
                color={projectRefreshSuccess ? 'success' : 'default'}
              >
                {props.projectsRefreshing ? (
                  <CircularProgress size={20} />
                ) : projectRefreshSuccess ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <DropdownContainer>
          <Dropdown
            value={getProjectLabel(props.sourceProjectId)}
            onChange={(e, newValue) => {
              if (newValue != null) handleSelectProject(newValue.id);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label ? option.label : option.id}
              </li>
            )}
            size={'small'}
            options={props.projects}
            renderInput={params => <TextField {...params} placeholder={'Select project'} />}
          />
        </DropdownContainer>
      </Grid>
    );
  };

  const renderComponentDropdown = () => {
    return (
      <>
        {!props.loopMode && (
          <Grid item xs={12}>
            <StyledLink to={`/project/canvas/${props.sourceProjectId}`} target="_blank">
              <OpenWorkflowText>
                <BlueTextCenter>
                  <StyledOpenInNewIcon />
                  Open {getProjectLabel(props.sourceProjectId)?.label} in a new tab
                </BlueTextCenter>
              </OpenWorkflowText>
            </StyledLink>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container alignItems="center" justifyContent="space-between">
            <InputLabel>
              {props.loopMode ? (
                <span className="alignItemCenter">
                  <BlackRedoIcon fontSize="small" />
                  Workflow loop ends at...
                </span>
              ) : (
                <>
                  Select the <BoldText>Trigger another Workflow</BoldText> component in the{' '}
                  <BoldText>Source Workflow</BoldText>
                </>
              )}
            </InputLabel>
            <Tooltip
              title={props.componentsRefreshing ? 'Refreshing components...' : 'Refresh components'}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleComponentRefresh}
                  aria-label="refresh components"
                  disabled={props.componentsRefreshing}
                  color={componentRefreshSuccess ? 'success' : 'default'}
                >
                  {props.componentsRefreshing ? (
                    <CircularProgress size={20} />
                  ) : componentRefreshSuccess ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <DropdownContainer>
            <Dropdown
              value={getComponentLabel(props.sourceComponentId)}
              onChange={(e, newValue) => {
                if (newValue != null) handleSelectComponent(newValue.id);
              }}
              size={'small'}
              options={props.components}
              renderInput={params => <TextField {...params} placeholder={'Select component'} />}
            />
          </DropdownContainer>
        </Grid>
      </>
    );
  };

  return (
    <>
      {props.projects && !props.loopMode && renderProjectDropdown()}
      {(props.sourceProjectId || props.loopMode) && renderComponentDropdown()}
    </>
  );
}

export default Dropdowns;
